import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { usePollWhileOffline } from '../lib/live';
import { isActive, LATE_MINUTES, laneOf, minutesSince } from '../lib/kitchen';
import { baht } from '../lib/text';
import type { Order } from '../lib/types';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

interface Table { id: string; name: string; seats: number; currentSessionId?: string; session: { id: string; guests: number; openedAt: string } | null }

/** /floor: every table as a tile (free / seated / cooking / eating / late); tap to seat, show the QR, or close. */
export function Floor({ path }: { path: string }) {
  const [tables, setTables] = useState<Table[]>([]);
  const [active, setActive] = useState<Order[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (token: string) => {
    try {
      const [t, k] = await Promise.all([
        api<{ tables: Table[] }>('GET', '/tables', { token }),
        api<{ orders: Order[] }>('GET', '/kitchen', { token }),
      ]);
      setTables(t.tables);
      setActive(k.orders);
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, []);

  const { s, online } = useStaff((e) => {
    if (e.kind === 'table' || e.kind === 'session') { if (s) void load(s.token); }
    if (e.kind === 'order') {
      const o = e.order as Order;
      setActive((prev) => [...prev.filter((x) => x.id !== o.id), ...(isActive(o) ? [o] : [])]);
    }
  }, () => { if (s) void load(s.token); });

  useEffect(() => { if (s) void load(s.token); }, [s, load]);
  usePollWhileOffline(online, () => { if (s) void load(s.token); });
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const bySession = useMemo(() => {
    const m = new Map<string, Order[]>();
    for (const o of active) m.set(o.sessionId, [...(m.get(o.sessionId) ?? []), o]);
    return m;
  }, [active]);

  const stateOf = (t: Table) => {
    if (!t.session) return { cls: 'free', label: 'ว่าง · Free' };
    const orders = bySession.get(t.session.id) ?? [];
    const cooking = orders.filter((o) => laneOf(o) === 'new' || laneOf(o) === 'cooking');
    if (cooking.some((o) => minutesSince(o.placedAt, now) >= LATE_MINUTES)) return { cls: 'late', label: 'ช้า · Late' };
    if (cooking.length) return { cls: 'cooking', label: 'รออาหาร · Cooking' };
    if (orders.length) return { cls: 'eating', label: 'พร้อมเสิร์ฟ · Ready' };
    return { cls: 'seated', label: 'นั่งแล้ว · Seated' };
  };

  const seated = tables.filter((t) => t.session).length;
  const selected = tables.find((t) => t.id === sel) ?? null;
  return (
    <Shell path={path} online={online} sub={`${seated}/${tables.length} tables`}>
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}
      <div className="layout">
        <section className="floorplan main" aria-label="Floor plan">
          <div className="tiles">
            {tables.map((t) => {
              const st = stateOf(t);
              return (
                <button key={t.id} className={`tile ${st.cls}`} aria-pressed={sel === t.id} onClick={() => setSel(t.id)} aria-label={`Table ${t.name}, ${st.label}`}>
                  <div className="row" style={{ width: '100%' }}>
                    <span className="display" style={{ fontSize: 20 }}>{t.name}</span>
                    <span className="tchip" style={st.cls === 'late' ? { background: 'var(--red)' } : undefined}>{st.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {t.session ? `${t.session.guests || '–'} guests · ${minutesSince(t.session.openedAt, now)} min` : `${t.seats} seats`}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
        <aside className="aside">
          {selected ? <TablePanel key={selected.id + (selected.session?.id ?? '')} t={selected} token={s?.token ?? ''} onChange={() => s && void load(s.token)} />
            : <div className="panel"><p style={{ margin: 0, color: '#A9AFC6' }}>แตะโต๊ะเพื่อเปิดหรือปิด · Tap a table to seat guests or close it.</p></div>}
        </aside>
      </div>
    </Shell>
  );
}

function TablePanel({ t, token, onChange }: { t: Table; token: string; onChange: () => void }) {
  const [guests, setGuests] = useState(2);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [qr, setQr] = useState('');
  const [bill, setBill] = useState<{ totalSatang: number; orders: Order[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!t.session) return;
    void api<{ joinUrl: string }>('GET', `/sessions/${t.session.id}/join`, { token }).then((r) => setJoinUrl(r.joinUrl), () => undefined);
    void api<{ totalSatang: number; orders: Order[] }>('GET', `/sessions/${t.session.id}/orders`, { token }).then(setBill, () => undefined);
  }, [t.session, token]);
  useEffect(() => {
    if (joinUrl) void QRCode.toString(joinUrl, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }).then(setQr);
    else setQr('');
  }, [joinUrl]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try { await fn(); onChange(); } catch (e) { handleAuthError(e); setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className="panel side" aria-label={`Table ${t.name}`}>
      <h2 className="display" style={{ margin: 0, fontSize: 22 }}>โต๊ะ {t.name}</h2>
      {!t.session ? (
        <>
          <label className="field" style={{ color: '#F2F0E9' }}>จำนวนลูกค้า · Guests
            <div className="stepper" style={{ alignSelf: 'flex-start', color: 'var(--ink)' }}>
              <button onClick={() => setGuests(Math.max(1, guests - 1))} aria-label="Fewer guests">−</button>
              <span>{guests}</span>
              <button onClick={() => setGuests(Math.min(100, guests + 1))} aria-label="More guests">+</button>
            </div>
          </label>
          <button className="btn-mango" disabled={busy} onClick={() => void act(async () => {
            const r = await api<{ joinUrl: string }>('POST', `/tables/${t.id}/sessions`, { token, body: { guests } });
            setJoinUrl(r.joinUrl);
          })}>เปิดโต๊ะ · Seat guests</button>
        </>
      ) : (
        <>
          {qr ? (
            <>
              <div className="qr" role="img" aria-label={`QR code to order at table ${t.name}`} dangerouslySetInnerHTML={{ __html: qr }} />
              <span style={{ fontSize: 13, color: '#A9AFC6', textAlign: 'center' }}>ให้ลูกค้าสแกนเพื่อสั่งอาหาร · Guests scan to order</span>
            </>
          ) : <div className="spinner" role="status" aria-label="Loading QR" style={{ alignSelf: 'center' }} />}
          {bill && (
            <div className="row" style={{ fontWeight: 700 }}>
              <span>{bill.orders.length} orders</span><span className="display" style={{ color: 'var(--mango)' }}>{baht(bill.totalSatang)}</span>
            </div>
          )}
          <button className="btn-dark" disabled={busy} onClick={() => void act(() => api('POST', `/sessions/${t.session!.id}/close`, { token }))}>ปิดโต๊ะ · Close table</button>
        </>
      )}
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>{err}</div>}
    </section>
  );
}
